/**
 * A medication is "stopped" when the patient has explicitly halted it via
 * the Stop action — which prepends "Stopped" (or "Stopped: <reason>") to
 * notes. A regimen whose endDate naturally arrives is just "completed,"
 * not stopped, and shouldn't be treated the same way.
 */
export function isMedicationStopped(notes: string | null | undefined): boolean {
  if (!notes) return false;
  return /^stopped(\s|:|$)/i.test(notes.trimStart());
}

/**
 * Prepends the stop marker used across medication history, factsheets, and
 * scheduling. Existing notes are preserved below the marker.
 */
export function prependStoppedMedicationNote(
  notes: string | null | undefined,
  reason?: string | null,
): string {
  const existingNotes = (notes ?? "").trim();
  if (isMedicationStopped(existingNotes)) return existingNotes || "Stopped";

  const trimmedReason = reason?.trim();
  const stopLine = trimmedReason ? `Stopped: ${trimmedReason}` : "Stopped";
  return existingNotes ? `${stopLine}\n${existingNotes}` : stopLine;
}

/**
 * Extracts the reason supplied to the Stop action, if any.
 * Returns null when there's no `Stopped: <reason>` prefix or the reason is
 * empty.
 */
export function parseStopReason(
  notes: string | null | undefined,
): string | null {
  if (!notes) return null;
  const match = notes.trimStart().match(/^stopped\s*:\s*([^\n]+)/i);
  const reason = match?.[1]?.trim();
  return reason ? reason : null;
}

/**
 * Returns the free-text portion of a note after the `Stopped[: reason]`
 * marker line. Non-stopped notes are returned unchanged. Returns null when
 * there is no body to show.
 */
export function parseStoppedNoteBody(
  notes: string | null | undefined,
): string | null {
  const trimmed = notes?.trim();
  if (!trimmed) return null;
  if (!isMedicationStopped(trimmed)) return trimmed;
  const newlineIndex = trimmed.indexOf("\n");
  if (newlineIndex === -1) return null;
  const body = trimmed.slice(newlineIndex + 1).trim();
  return body ? body : null;
}
